// @LTG-ID: F-023
// @STATUS: BUILT
// @HERMETIC: false  // consumes ExternalCTA + ASK_QUESTIONS data
// @DOCS: 08_FEATURES.md#F-023, source Landing Page.html lines 1987-2071
//        Deviations: ADR-012 D6 (cancel-on-question-switch — source already does
//        this; we add useEffect unmount-cleanup as the formal D6 implementation),
//        ADR-012 D7 (mobile <560px full-width bottom-sheet — CSS-only deviation),
//        ADR-012 D9 (a11y enhancements: ESC closes, outside-click closes, focus
//        trap on open — these are new vs source).
// @CHANGED: 2026-02 session_003 (CHG-0053)
// @OWNER: web
//
// F-023 — Floating Q&A widget. Bottom-left FAB; click opens a panel listing the
// 9 ASK_QUESTIONS; clicking a question types its answer character-by-character
// at 3 chars / 12ms (source-faithful). One ExternalCTA at the bottom of the
// answer view drives `ask-widget-order` (pre-reserved in `lib/cta-ids.js`).
//
// Z-index map (avoids collision with bottom-right ScrollTopFAB at z=60):
//   FAB:   z=70  (above panel so close-X on FAB stays clickable)
//   Panel: z=69
//   ScrollTopFAB: z=60  (separate corner; no spatial collision either)
//   Header: z=50

import { useState, useRef, useEffect, useCallback } from "react";
import { ASK_QUESTIONS } from "../../data/ask-questions";
import ExternalCTA from "../../lib/external-cta";
import "./ask-widget.css";

export function AskWidget() {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const [typed, setTyped] = useState("");
    const typingRef = useRef(null);
    const panelRef = useRef(null);
    const fabRef = useRef(null);
    const prevFocusRef = useRef(null);

    // ADR-012 D6 — clear in-flight typewriter on unmount (source already clears
    // on question switch; we additionally clear on unmount so a fast route change
    // doesn't leak the interval).
    useEffect(() => {
        return () => {
            if (typingRef.current) {
                clearInterval(typingRef.current);
                typingRef.current = null;
            }
        };
    }, []);

    // ADR-012 D9a — ESC closes panel.
    // ADR-012 D9b — pointerdown outside panel + outside FAB closes panel.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        const onPointerDown = (e) => {
            if (panelRef.current?.contains(e.target)) return;
            if (fabRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("pointerdown", onPointerDown);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("pointerdown", onPointerDown);
        };
    }, [open]);

    // ADR-012 D9c — minimal focus trap. On open, remember the prior focused
    // element and move focus into the panel (close-X). On close, restore.
    useEffect(() => {
        if (open) {
            prevFocusRef.current = document.activeElement;
            // Defer to next tick so the panel is in the DOM.
            const id = requestAnimationFrame(() => {
                const closeBtn = panelRef.current?.querySelector(
                    "[data-testid='ask-widget-close']",
                );
                closeBtn?.focus();
            });
            return () => cancelAnimationFrame(id);
        } else if (prevFocusRef.current && prevFocusRef.current.focus) {
            prevFocusRef.current.focus();
        }
    }, [open]);

    const chooseQuestion = useCallback((i) => {
        setActive(i);
        setTyped("");
        if (typingRef.current) {
            clearInterval(typingRef.current);
            typingRef.current = null;
        }
        const text = ASK_QUESTIONS[i].a;
        let pos = 0;
        typingRef.current = setInterval(() => {
            pos += 3;
            if (pos >= text.length) {
                setTyped(text);
                clearInterval(typingRef.current);
                typingRef.current = null;
            } else {
                setTyped(text.slice(0, pos));
            }
        }, 12);
    }, []);

    const back = useCallback(() => {
        setActive(-1);
        setTyped("");
        if (typingRef.current) {
            clearInterval(typingRef.current);
            typingRef.current = null;
        }
    }, []);

    return (
        <>
            <button
                ref={fabRef}
                className={"ltg-ask-fab" + (open ? " is-open" : "")}
                onClick={() => setOpen((o) => !o)}
                aria-label={open ? "Close help" : "Open help"}
                aria-expanded={open}
                aria-controls="ltg-ask-panel"
                data-testid="ask-widget-fab"
            >
                {open ? (
                    "\u00D7"
                ) : (
                    <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                    </svg>
                )}
            </button>

            {open && (
                <div
                    ref={panelRef}
                    id="ltg-ask-panel"
                    className="ltg-ask-panel"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Quick answers"
                    data-testid="ask-widget-panel"
                >
                    <div className="ltg-ask-head">
                        <div className="ltg-ask-head-l">
                            <span
                                className="ltg-ask-head-dot"
                                aria-hidden="true"
                            ></span>
                            <span className="ltg-ask-head-title">
                                Quick answers
                            </span>
                        </div>
                        <button
                            className="ltg-ask-close"
                            onClick={() => setOpen(false)}
                            aria-label="Close help"
                            data-testid="ask-widget-close"
                        >
                            &times;
                        </button>
                    </div>
                    <div className="ltg-ask-body">
                        {active === -1 ? (
                            <>
                                <div className="ltg-ask-greeting">
                                    <p>
                                        What would you like to know? Pick a
                                        question, and the answer appears below.
                                    </p>
                                </div>
                                <div
                                    className="ltg-ask-list"
                                    data-testid="ask-widget-list"
                                >
                                    {ASK_QUESTIONS.map((qa, i) => (
                                        <button
                                            key={i}
                                            className="ltg-ask-q"
                                            onClick={() => chooseQuestion(i)}
                                            data-testid={`ask-widget-q-${i}`}
                                        >
                                            {qa.q}
                                        </button>
                                    ))}
                                </div>
                                <p className="ltg-ask-foot">
                                    These are curated answers from the firm.
                                    No AI guessing, no scripts, no chat with
                                    strangers.
                                </p>
                            </>
                        ) : (
                            <>
                                <button
                                    className="ltg-ask-back"
                                    onClick={back}
                                    data-testid="ask-widget-back"
                                >
                                    &larr; All questions
                                </button>
                                <div
                                    className="ltg-ask-thread"
                                    data-testid="ask-widget-thread"
                                >
                                    <div className="ltg-ask-bubble ltg-ask-bubble--owner">
                                        {ASK_QUESTIONS[active].q}
                                    </div>
                                    <div
                                        className="ltg-ask-bubble ltg-ask-bubble--firm"
                                        data-testid="ask-widget-answer"
                                    >
                                        {typed || "…"}
                                    </div>
                                </div>
                                <div className="ltg-ask-cta-row">
                                    <ExternalCTA
                                        ctaId="ask-widget-order"
                                        className="ltg-ask-cta"
                                        data-testid="ask-widget-primary"
                                    >
                                        Get Your Valuation &rarr;
                                    </ExternalCTA>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

export default AskWidget;
